import './shared/string.extensions'
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

// Colores corporativos Yobing (del isotipo en assets/logo-yobing.svg):
// azul #1D91E8 como primario; ver tailwind.config.js para la misma rampa
// usada en las plantillas con clases blue-*/primary-*.
const YobingPreset = definePreset(Aura, {
    primitive: {
        ...Aura.primitive,
        yobing: {
            50:  '#EFF8FE',
            100: '#DFF1FD',
            200: '#B9E3FB',
            300: '#7DCBF7',
            400: '#3DAEEF',
            500: '#1D91E8',
            600: '#1073C4',
            700: '#0D5B9E',
            800: '#0F4A7C',
            900: '#113E63',
            950: '#0B283F',
        },
        // Verde/rojo semanticos (crear/eliminar) retocados para acompañar la
        // paleta de marca: menos saturados que el verde/rojo por defecto de
        // PrimeNG, sin perder el significado (positivo/destructivo).
        green: {
            50:  '#EAFBF3',
            100: '#D2F5E3',
            200: '#A6EBC8',
            300: '#6FDBA8',
            400: '#3DC088',
            500: '#1F9D6B',
            600: '#167D57',
            700: '#126347',
            800: '#124F3B',
            900: '#0F4131',
            950: '#0A2B20',
        },
        red: {
            50:  '#FDECEC',
            100: '#FBD5D5',
            200: '#F7ABAB',
            300: '#F17E7E',
            400: '#E85C5C',
            500: '#DC3B3B',
            600: '#BC2929',
            700: '#9B2222',
            800: '#7E1E1E',
            900: '#691C1C',
            950: '#450F0F',
        }
    } as any,
    semantic: {
        // Global de PrimeNG para CUALQUIER elemento deshabilitado (opacity), por defecto 0.6 -
        // combinado con colores ya de por sí poco saturados, dejaba botones deshabilitados
        // (p.ej. "Alinear" sin selección) casi ilegibles. Subido para que siga distinguiéndose
        // "deshabilitado" sin dejar de poder leerse.
        disabledOpacity: '0.85',
        // 500/600 son los que usan los botones/focus rings en claro (Aura
        // usa un tono más claro, p.ej. 400, para el esquema oscuro) — se
        // fuerzan al navy del sidebar/cabeceras de tabla en vez del azul
        // vivo de la rampa, a petición explícita: "el primario debe ser
        // el mismo que el de las cabeceras de las tablas".
        primary: {
            50:  '{yobing.50}',
            100: '{yobing.100}',
            200: '{yobing.200}',
            300: '{yobing.300}',
            400: '{yobing.400}',
            500: '{yobing.900}',
            600: '{yobing.950}',
            700: '{yobing.950}',
            800: '{yobing.800}',
            900: '{yobing.900}',
            950: '{yobing.950}',
        }
    },
    components: {
        // Los botones "secondary" (severity="secondary"/p-button-secondary) usan por defecto
        // {surface.100} de fondo y {surface.600} de texto - muy poco contraste en claro contra un
        // fondo blanco (reportado como difícil de leer, más aún con daltonismo). Es un ajuste de
        // luminosidad, no de tono, así que ayuda por igual independientemente del tipo de
        // daltonismo. No se toca el esquema oscuro (ya tenía contraste suficiente).
        button: {
            colorScheme: {
                light: {
                    root: {
                        secondary: {
                            background: '{yobing.100}',
                            hoverBackground: '{yobing.200}',
                            activeBackground: '{yobing.300}',
                            borderColor: '{yobing.200}',
                            hoverBorderColor: '{yobing.300}',
                            activeBorderColor: '{yobing.400}',
                            color: '{yobing.900}',
                            hoverColor: '{yobing.950}',
                            activeColor: '{yobing.950}',
                        },
                    },
                },
            },
        },
    },
});

import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { CarouselModule } from 'primeng/carousel';
import { ProgressBarModule } from 'primeng/progressbar';
import { ChartModule } from 'primeng/chart';
import { AvatarModule } from 'primeng/avatar';
import { AccordionModule } from 'primeng/accordion';

// Application Components
import { AppComponent, createTranslateLoader } from './app.component';
import { AppBreadcrumbComponent } from 'src/app/layout/breadcrumb/app.breadcrumb.component';
import { AppMainComponent } from './app.main.component';
import { AppInlineMenuComponent } from './layout/inlinemenu/app.inlinemenu.component';
import { AppMenuComponent } from './layout/menu/app.menu.component';
import { AppMenuitemComponent } from './layout/menu/app.menuitem.component';
import { AppTopbarComponent } from './layout/topbar/app.topbar.component';
import { AppFooterComponent } from './layout/footer/app.footer.component';

import { AppHelpComponent } from './pages/app.help.component';
import { AppNotfoundComponent } from './pages/app.notfound.component';
import { AppErrorComponent } from './pages/app.error.component';
import { AppAccessdeniedComponent } from './pages/app.accessdenied.component';
import { AssistantComponent } from './pages/assistant/assistant.component';
import { AssistantService } from './pages/assistant/assistant.service';
import { TextareaModule } from 'primeng/textarea';

// Application services
import { MenuService } from './layout/menu/app.menu.service';
import { AppBreadcrumbService } from './layout/breadcrumb/app.breadcrumb.service';

import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import {
    LucideAngularModule,
    LayoutDashboard,
    LifeBuoy,
    Building,
    Building2,
    Users,
    Clock,
    Calendar,
    CalendarOff,
    CalendarClock,
    Inbox,
    Euro,
    Monitor,
    FileBarChart2,
    Settings,
    ShieldCheck,
    ClipboardList,
    ScrollText,
    Landmark,
    History,
    RefreshCw,
} from 'lucide-angular';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import localeEs from '@angular/common/locales/es';
import localeExtraEs from '@angular/common/locales/extra/es';

import { registerLocaleData } from '@angular/common';
import { NgxPermissionsModule } from 'ngx-permissions';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastNotifierService } from './shared/toast-notifier.service';

import { FIREBASE_OPTIONS } from '@angular/fire/compat';

import { USE_EMULATOR as USE_AUTH_EMULATOR } from '@angular/fire/compat/auth';
import { connectFirestoreEmulator, getFirestore, provideFirestore } from '@angular/fire/firestore';

import { environment } from '../environments/environment';
import { AuthService } from './auth/auth.service';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { ConfirmDialogService } from './shared/confirm-dialog.service';
import { TokenInterceptor } from './auth/auth.interceptor';
import { LatestVersionsModule } from './components/latest-versions/latest-versions.module';
import { StatusModule } from './pages/status/status.module';
import { ProfileService } from './shared/profile.service';
import { SharedUserDataService } from './shared/shared-user-data.service';
import { AppInitService } from './app-init-service';
import { WelcomeModule } from './components/welcome/welcome.module';
import { CustomAvatarModule } from './components/avatar/avatar.module';
import { FirebaseService } from './core/services/firebase.service';
import { CompanyService } from './pages/company/company.service';
import { GlobalErrorHandler } from './core/global-error.handler';
registerLocaleData(localeEs, 'es-ES', localeExtraEs);

const appConfigFactory = (appInitService: AppInitService) => {
  return () => {
    appInitService.init();
  };
}

@NgModule({ declarations: [
        AppComponent,
        AppBreadcrumbComponent,
        AppMainComponent,
        AppMenuComponent,
        AppMenuitemComponent,
        AppInlineMenuComponent,
        AppTopbarComponent,
        AppFooterComponent,
        AppHelpComponent,
        AppNotfoundComponent,
        AppErrorComponent,
        AppAccessdeniedComponent,
        AssistantComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        FormsModule,
        AppRoutingModule,
        BreadcrumbModule,
        ButtonModule,
        TextareaModule,
        CardModule,
        ConfirmDialogModule,
        TableModule,
        CarouselModule,
        ProgressBarModule,
        ChartModule,
        SelectModule,
        AvatarModule,
        AccordionModule,
        DatePickerModule,
        InputTextModule,
        InputNumberModule,
        ToggleSwitchModule,
        ToastModule,
        FontAwesomeModule,
        LucideAngularModule.pick({
            LayoutDashboard,
            LifeBuoy,
            Building,
            Building2,
            Users,
            Clock,
            Calendar,
            CalendarOff,
            CalendarClock,
            Inbox,
            Euro,
            Monitor,
            FileBarChart2,
            Settings,
            ShieldCheck,
            ClipboardList,
            ScrollText,
            Landmark,
            History,
            RefreshCw,
        }),
        AvatarModule,
        DialogModule,
        LatestVersionsModule,
        StatusModule,
        WelcomeModule,
        CustomAvatarModule,
        NgxPermissionsModule.forRoot(),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (createTranslateLoader),
                deps: [HttpClient]
            }
        })], providers: [
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideFirestore(() => {
            const firestore = getFirestore();
            if (environment.useEmulators)
                connectFirestoreEmulator(firestore, 'localhost', 18081);
            return firestore;
        }),
        AuthService,
        MenuService,
        AppBreadcrumbService,
        MessageService,
        ConfirmDialogService,
        ConfirmationService,
        ProfileService,
        FirebaseService,
        SharedUserDataService,
        ToastNotifierService,
        CompanyService,
        AssistantService,
        /* {
           provide: ErrorHandler,
           useValue: Sentry.createErrorHandler({
             showDialog: false,
           }),
         },
         {
           provide: Sentry.TraceService,
           deps: [Router],
         },*/
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        {
            provide: APP_INITIALIZER,
            useFactory: appConfigFactory,
            deps: [AppInitService],
            multi: true,
        },
        {
            provide: LOCALE_ID,
            useValue: "es-ES"
        },
        { provide: FIREBASE_OPTIONS, useValue: environment.firebase },
        { provide: USE_AUTH_EMULATOR, useValue: environment.useEmulators ? ['http://localhost', 19099] : undefined },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimationsAsync(),
        providePrimeNG({ 
            theme: { 
                preset: YobingPreset, 
                options: { darkModeSelector: '.app-dark' } 
            } 
        }),
    ] })

export class AppModule {
  constructor(library: FaIconLibrary) {
    // Add an icon to the library for convenient access in other components
    library.addIconPacks(fas, far);
  }
}


